import { useCallback, useMemo, useState } from "react";
import { parseCommentAnchor } from "@hightouch/lib/llm/asset-comments/types";
import type { AmpLibraryFileFragment } from "src/graphql";
import { slideCommentLabel } from "src/pages/projects/library/files/slide-comment-label";

export function useSlideComments(
  file: Pick<AmpLibraryFileFragment, "type" | "slides">,
  selectionKey = "latest",
) {
  const [selection, setSelection] = useState<{
    key: string;
    slideId: string | null;
  }>({ key: selectionKey, slideId: null });
  if (selection.key !== selectionKey) {
    setSelection({ key: selectionKey, slideId: null });
  }
  const selectedSlideId =
    selection.key === selectionKey ? selection.slideId : null;
  const preview = useMemo(
    () =>
      file.type === "slides"
        ? {
            selectedSlideId,
            onSelectSlide: (slideId: string) =>
              setSelection({ key: selectionKey, slideId }),
          }
        : undefined,
    [file.type, selectedSlideId, selectionKey],
  );
  const slideIds = file.slides?.slideIds ?? [];
  const slideId = preview ? (selectedSlideId ?? slideIds[0]) : undefined;
  const anchor = slideId ? { kind: "slide" as const, slideId } : null;
  const labelForThread = (thread: { anchor?: unknown }) =>
    preview ? slideCommentLabel(thread.anchor, slideIds) : null;
  const selectThread = useCallback(
    (value: unknown) => {
      const target = parseCommentAnchor(value);
      if (file.type === "slides" && target?.kind === "slide") {
        setSelection({ key: selectionKey, slideId: target.slideId });
      }
    },
    [file.type, selectionKey],
  );

  return {
    preview,
    anchor,
    composerKey: slideId ?? "file",
    canComment: !slideId || slideIds.includes(slideId),
    label: labelForThread({ anchor }),
    labelForThread,
    selectThread,
  };
}
